import { Component, EventEmitter, OnInit, Output, inject, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { ProcessService } from '../../services/process.service';
import { CatalogService } from '@services/data/catalog.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { BottomSheetComponent } from 'app/core/components/bottom-sheet/bottom-sheet.component';
import Swal from 'sweetalert2';
import { PermissionService } from '@services/core/permission.service';

@Component({
  selector: 'app-process-list',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, ListCardComponent, MobileFooterComponent, BottomSheetComponent],
  templateUrl: './process-list.component.html',
  styleUrl: './process-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class ProcessListComponent implements OnInit {
  @Output() selectEvent = new EventEmitter<any>();

  processes = signal<any[]>([]);
  filteredProcesses: any[] = [];
  selectedUuid = signal<string | null>(null);
  isLoading = false;
  rows = Array.from({ length: 5 });

  categoryFilter: string | null = null;
  searchText = '';
  mobileFiltersOpen = false;
  orderColumn: string = 'name';
  orderDirection: 'asc' | 'desc' = 'asc';

  public appSettings = inject(AppSettings);
  private processService = inject(ProcessService);
  private catalogService = inject(CatalogService);
  readonly perm = inject(PermissionService);

  categories = computed(() =>
    (this.catalogService.processCategories() || []).map((c: any) => ({
      value: c.slug,
      label: `${c.name} (${c.slug})`
    }))
  );

  ngOnInit(): void {
    this.loadProcesses();
  }

  // Carga la lista de procesos desde el servicio
  loadProcesses(): void {
    this.isLoading = true;
    this.processService.get().subscribe({
      next: (data) => {
        this.processes.set(data ?? []);
        this.applyFilters();
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  // Aplica filtros de categoria y texto de busqueda
  applyFilters(): void {
    const list = this.processes();
    const query = this.searchText.trim().toLowerCase();
    this.filteredProcesses = list.filter((p) => {
      const matchCat = !this.categoryFilter || p.category === this.categoryFilter;
      const matchText = !query ||
        (p.name && p.name.toLowerCase().includes(query)) ||
        (p.slug && p.slug.toLowerCase().includes(query)) ||
        (p.abbr && p.abbr.toLowerCase().includes(query));
      return matchCat && matchText;
    });
    this.sortData();
  }

  // Ordena columnas de forma ascendente o descendente
  orderCol(column: string): void {
    if (this.orderColumn === column) {
      this.orderDirection = this.orderDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.orderColumn = column;
      this.orderDirection = 'asc';
    }
    this.sortData();
  }

  // Aplica algoritmo de ordenamiento sobre filteredProcesses
  private sortData(): void {
    const col = this.orderColumn;
    const factor = this.orderDirection === 'asc' ? 1 : -1;
    this.filteredProcesses.sort((a, b) => {
      const valA = a[col] ?? '';
      const valB = b[col] ?? '';
      return valA > valB ? factor : (valA < valB ? -factor : 0);
    });
  }

  // Emite el proceso seleccionado para edicion
  selectProcess(process: any): void {
    this.selectedUuid.set(process?.uuid ?? null);
    this.selectEvent.emit(process);
  }

  setCategoryFilter(slug: string | null): void {
    this.categoryFilter = slug;
    this.applyFilters();
  }

  categoryLabel(proc: any): string {
    return proc?.processCategory?.name || proc?.category || 'SIN CAT';
  }

  settlementLabel(target: string | null | undefined): string {
    switch (String(target || 'NONE').toUpperCase()) {
      case 'ENTITY_PAYMENT': return 'Pago entidad';
      case 'REIMBURSEMENT': return 'Reembolso';
      default: return 'Ninguno';
    }
  }

  // Confirma y elimina un proceso
  deleteProcess(process: any): void {
    Swal.fire({
      title: `¿Eliminar proceso "${process.name}"?`,
      text: 'Esta acción no se puede deshacer si no está en uso.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((res) => {
      if (res.isConfirmed) this.executeDelete(process.uuid);
    });
  }

  // Ejecuta llamada de eliminacion en el backend por UUID
  private executeDelete(uuid: string): void {
    this.processService.delete(uuid).subscribe({
      next: () => {
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Proceso eliminado', timer: 3000 });
        this.loadProcesses();
      },
      error: (err) => {
        Swal.fire('Error', err?.error?.message ?? 'No se pudo eliminar el proceso', 'error');
      }
    });
  }
}
